import useApiClient from 'hooks/use-api-client';
import type { UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getQueryClientInstance } from 'stores/rq/common/query-client-provider';
import isString from 'lodash/isString';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { returnData } from 'services/api-client/client';
import type { EnvironmentApi } from '@env0/environment-service/api';
import type { Environment } from 'types/api.types';
import { invalidateAllActiveEnvironments } from 'components/dashboard/widgets/use-all-active-environments';
import useStores from 'hooks/use-stores.hooks';

export const useGetEnvironment = (environmentId?: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environment', environmentId],
    queryFn: () => apiClient.environments.getEnvironment(environmentId!),
    enabled: isString(environmentId)
  });
};

const useEnvironmentTagsCacheKey = () => {
  const orgId = useCurrentOrganizationId();

  return [orgId, 'environments', 'tags'] as const;
};

export const useGetEnvironmentTags = () => {
  const apiClient = useApiClient();
  const queryKey = useEnvironmentTagsCacheKey();
  const [orgId] = queryKey;

  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [organizationId] }) => apiClient.environments.findTagsByOrganizationId(organizationId),
    enabled: Boolean(orgId),
    placeholderData: {}
  });
};

export const useUpdateEnvironmentTags = (environmentId: string) => {
  const apiClient = useApiClient();
  const queryClient = getQueryClientInstance();
  const orgId = useCurrentOrganizationId();
  const tagsKey = useEnvironmentTagsCacheKey();
  const { environmentsStore } = useStores();

  return useMutation({
    mutationFn: (patch: EnvironmentApi.UpdateTags.Request.Body) =>
      apiClient.environments.updateTags(environmentId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagsKey });
      queryClient.invalidateQueries({ queryKey: [orgId, 'environment', environmentId] });
      // Neither key above is read by the Explorer/dashboard table (React Query) or the
      // MobX-backed Environments list, so both go stale after a tags save unless poked directly.
      invalidateAllActiveEnvironments(orgId);
      environmentsStore.getEnvironment(environmentId);
    }
  });
};

export const useGetRunsUsage = () => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'runs-usage'],
    queryFn: () => apiClient.environments.getRunsUsage(orgId),
    enabled: !!orgId
  });
};

export const useGetEnvironmentNameify = (environmentId: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environment-nameify', environmentId],
    queryFn: () =>
      apiClient.environments.getEnvironment(environmentId!, {
        hideNotification: true,
        params: { fields: 'name,projectId' }
      }),
    staleTime: Infinity
  });
};

export const useSearchEnvironment = (searchText: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environment-search', searchText],
    queryFn: () =>
      apiClient.environments
        .getAll({
          organizationId: orgId,
          limit: 19,
          searchText,
          onlyMy: false,
          isActive: true
        })
        .then(returnData),
    enabled: !!searchText
  });
};

export const ACTIVE_PROJECT_ENVIRONMENTS_PREVIEW_LIMIT = 5;

export const useGetActiveProjectEnvironments = (projectId: string, { enabled }: { enabled: boolean }) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environments', 'active-by-project', projectId],
    queryFn: async () => {
      const [{ data: count }, { data: environments }] = await Promise.all([
        apiClient.environments.countActive(projectId),
        apiClient.environments.getAll({
          projectId,
          onlyMy: false,
          isActive: true,
          limit: ACTIVE_PROJECT_ENVIRONMENTS_PREVIEW_LIMIT
        })
      ]);
      return { count, environments };
    },
    enabled
  });
};

const environmentsByDriftStatusCacheKey = (
  orgId: string,
  driftStatus: EnvironmentApi.DriftStatus[],
  projectId?: string
) => [orgId, 'environments', 'by-drift-status', { driftStatus: [...driftStatus].sort(), projectId }] as const;

export const useGetEnvironmentsByDriftStatus = ({
  driftStatus,
  projectId
}: {
  driftStatus: EnvironmentApi.DriftStatus[];
  projectId?: string;
}): UseQueryResult<Environment[]> => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: environmentsByDriftStatusCacheKey(orgId, driftStatus, projectId),
    queryFn: () =>
      apiClient.environments
        .getAll(
          {
            ...(projectId ? { projectId } : { organizationId: orgId }),
            driftStatus,
            onlyMy: false,
            isActive: true
          },
          { hideNotification: true }
        )
        .then(returnData),
    enabled: Boolean(orgId)
  });
};

export const useGetLatestDriftDeployment = (environmentId: string) => {
  const orgId = useCurrentOrganizationId();
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [orgId, 'environment', environmentId, 'latest-drift'],
    queryFn: () =>
      apiClient.environments
        .getEnvironmentDeployments(environmentId)
        .then(response =>
          response.data.find(deployment => deployment.type === 'driftDetection' && deployment.status === 'SUCCESS')
        )
  });
};

export const useGetDriftedResourceEvents = ({
  enabled = true,
  deploymentId,
  resourceType,
  resourceId
}: {
  enabled: boolean;
  deploymentId: string;
  resourceType?: string;
  resourceId?: string;
}) => {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['cloud-resources', 'drift-blame-events', { deploymentId, resourceType, resourceId }],
    enabled: enabled && !!resourceType && !!resourceId,
    queryFn: async () => {
      return apiClient.environments.getDriftedResourceEvents(deploymentId, resourceType!, resourceId!);
    }
  });
};
